import { useCallback, useEffect, useRef, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────

export type DirectorMode = 'stream' | 'design' | 'studio';

export type MessageRole = 'user' | 'assistant' | 'system';

export interface ToolUse {
  id: string;
  name: string;
  params: Record<string, unknown>;
}

export interface ToolResult {
  id: string;
  name: string;
  result: string;
  success: boolean;
}

export interface DirectorMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: number;
  toolUse?: ToolUse;
  toolResult?: ToolResult;
  isStreaming?: boolean;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface UseDirectorOptions {
  eventId?: string;
  mode?: DirectorMode;
  onToolUse?: (toolUse: ToolUse) => void;
  onToolResult?: (toolResult: ToolResult) => void;
  onError?: (error: string) => void;
}

export interface UseDirectorReturn {
  messages: DirectorMessage[];
  isStreaming: boolean;
  connectionStatus: ConnectionStatus;
  sendMessage: (content: string) => void;
  clearMessages: () => void;
  retryConnection: () => void;
}

// ─── Hook Implementation ──────────────────────────────────────────

function getDirectorWsUrl(): string {
  const explicit = (import.meta as any).env.VITE_DIRECTOR_WS_URL;
  if (explicit) return explicit;
  // Derive from API base URL: http://localhost:3000/api/v1 → ws://localhost:8100/ws
  const apiBase = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
  const url = new URL(apiBase);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.hostname}:8100/ws`;
}

export function useDirector({
  eventId,
  mode = 'design',
  onToolUse,
  onToolResult,
  onError,
}: UseDirectorOptions = {}): UseDirectorReturn {
  const [messages, setMessages] = useState<DirectorMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');

  const wsRef = useRef<WebSocket | null>(null);
  const messagesRef = useRef<DirectorMessage[]>([]);
  const currentStreamingIdRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const pendingMessagesRef = useRef<string[]>([]);

  // Keep messagesRef in sync for use in callbacks
  messagesRef.current = messages;

  // Stabilize callbacks in refs to avoid re-render loops
  const onToolUseRef = useRef(onToolUse);
  const onToolResultRef = useRef(onToolResult);
  const onErrorRef = useRef(onError);
  onToolUseRef.current = onToolUse;
  onToolResultRef.current = onToolResult;
  onErrorRef.current = onError;

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const flushPendingMessages = useCallback(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;

    while (pendingMessagesRef.current.length > 0) {
      const content = pendingMessagesRef.current.shift();
      if (!content) continue;
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content,
      }));
    }
  }, []);

  // Connect directly to Director Python service WebSocket
  const connect = useCallback(() => {
    if (!eventId) {
      setConnectionStatus('disconnected');
      return;
    }

    cleanup();
    setConnectionStatus('connecting');

    const wsUrl = getDirectorWsUrl();
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      // Send init message per Director WS protocol
      const token = localStorage.getItem('sb_token');
      ws.send(JSON.stringify({
        type: 'init',
        event_id: eventId,
        mode,
        auth_token: token || undefined,
      }));
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (connectionStatus !== 'disconnected') {
        setConnectionStatus('disconnected');
      }
      // Auto-reconnect with backoff if we still have an eventId
      if (eventId && reconnectAttemptsRef.current < 10) {
        const backoff = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current++), 10000);
        reconnectTimeoutRef.current = setTimeout(() => connect(), backoff);
      }
    };

    ws.onerror = () => {
      // onclose will fire after this
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleMessage(data);
      } catch {
        // ignore parse errors
      }
    };
  }, [eventId, mode, cleanup]);

  const handleMessage = useCallback((data: any) => {
    switch (data.type) {
      case 'status': {
        // Director confirmed connection
        setConnectionStatus('connected');
        flushPendingMessages();
        break;
      }

      case 'token': {
        // Streaming token — append to current assistant message
        const streamingId = currentStreamingIdRef.current;
        const tokenContent = data.token || data.content || '';
        if (!streamingId) {
          const newId = `assistant-${Date.now()}`;
          currentStreamingIdRef.current = newId;
          setMessages((prev) => [
            ...prev,
            {
              id: newId,
              role: 'assistant',
              content: tokenContent,
              timestamp: Date.now(),
              isStreaming: true,
            },
          ]);
          setIsStreaming(true);
        } else {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingId
                ? { ...msg, content: msg.content + tokenContent }
                : msg
            )
          );
        }
        break;
      }

      case 'tool_call': {
        const toolUse: ToolUse = {
          id: data.tool_id || `tool-${Date.now()}`,
          name: data.tool_name || 'unknown',
          params: data.input || {},
        };

        setMessages((prev) => [
          ...prev,
          {
            id: `tooluse-${toolUse.id}`,
            role: 'assistant',
            content: '',
            timestamp: Date.now(),
            toolUse,
          },
        ]);

        onToolUseRef.current?.(toolUse);
        break;
      }

      case 'tool_output': {
        // Look up tool name from the matching tool_call message
        const matchingToolCall = messagesRef.current.find(
          (msg) => msg.toolUse?.id === (data.tool_id || '')
        );
        const toolResult: ToolResult = {
          id: data.tool_id || `result-${Date.now()}`,
          name: data.tool_name || matchingToolCall?.toolUse?.name || 'tool',
          result: typeof data.output === 'string' ? data.output : JSON.stringify(data.output || ''),
          success: !data.is_error,
        };

        setMessages((prev) => {
          const toolUseIndex = prev.findIndex(
            (msg) => msg.toolUse?.id === toolResult.id
          );
          if (toolUseIndex >= 0) {
            const updated = [...prev];
            updated[toolUseIndex] = {
              ...updated[toolUseIndex],
              toolResult,
            };
            return updated;
          }
          return [
            ...prev,
            {
              id: `toolresult-${toolResult.id}`,
              role: 'assistant',
              content: '',
              timestamp: Date.now(),
              toolResult,
            },
          ];
        });

        onToolResultRef.current?.(toolResult);
        break;
      }

      case 'complete': {
        const streamingId = currentStreamingIdRef.current;
        if (streamingId) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingId ? { ...msg, isStreaming: false } : msg
            )
          );
        }
        currentStreamingIdRef.current = null;
        setIsStreaming(false);
        break;
      }

      case 'error': {
        const errorMsg = data.error || data.message || 'An error occurred';
        currentStreamingIdRef.current = null;
        setIsStreaming(false);
        onErrorRef.current?.(errorMsg);

        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'system',
            content: errorMsg,
            timestamp: Date.now(),
          },
        ]);
        break;
      }

      case 'heartbeat': {
        // Keep-alive, ignore
        break;
      }

      default:
        break;
    }
  }, [flushPendingMessages]);

  // Send a message to the Director
  const sendMessage = useCallback(
    (content: string) => {
      if (!eventId || !content.trim()) return;
      const trimmedContent = content.trim();

      // Add user message to the list
      const userMessage: DirectorMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: trimmedContent,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Send via direct WebSocket to Director service
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'message',
          content: trimmedContent,
        }));
      } else {
        pendingMessagesRef.current.push(trimmedContent);
      }
    },
    [eventId]
  );

  // Clear all messages
  const clearMessages = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'clear' }));
    }
    pendingMessagesRef.current = [];
    setMessages([]);
    currentStreamingIdRef.current = null;
    setIsStreaming(false);
  }, []);

  // Retry connection
  const retryConnection = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  // Connect on mount and when eventId changes
  useEffect(() => {
    connect();
    return () => {
      cleanup();
      setConnectionStatus('disconnected');
    };
  }, [connect, cleanup]);

  return {
    messages,
    isStreaming,
    connectionStatus,
    sendMessage,
    clearMessages,
    retryConnection,
  };
}
